import { useEffect, useRef, useState } from 'react';
import { Spin, Typography } from 'antd';
import { space } from '@/design/tokens.js';

const { Text } = Typography;

export interface PolicyDocumentProps {
  /** The PDF itself, already fetched with the session attached. */
  file: Blob;
  title: string;
  onFailure: (message: string) => void;
}

/**
 * A policy PDF, drawn by us rather than by the browser (orbit-ui#185).
 *
 * **An `<iframe>` does not show a PDF on a phone.** Desktop browsers have a
 * built-in viewer and will happily frame one; iOS Safari and Android Chrome
 * do not, and render a placeholder with an "Open" button instead — which
 * does nothing at all for a `blob:` URL. That was the whole of #185: the
 * document opened on a laptop and was a dead button on a phone.
 *
 * So the pages are rasterised onto canvases. It is the one approach that
 * behaves the same everywhere, and it keeps the document inside the window
 * that asked somebody to read it rather than handing it to another app.
 *
 * **The first page is shown as soon as it is drawn** (orbit-ui#184). The old
 * reader waited for the whole file before showing anything, so a long policy
 * was a spinner for several seconds; a reader only ever looks at page one
 * first, and the rest arrive underneath while they are reading it.
 *
 * **pdf.js is loaded on demand.** It is a large library and most sessions
 * never open a policy, so it is behind a dynamic import and costs nothing
 * until somebody presses Read.
 */
export function PolicyDocument({ file, title, onFailure }: PolicyDocumentProps) {
  const container = useRef<HTMLDivElement>(null);
  const [pagesDrawn, setPagesDrawn] = useState(0);
  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const canvases: HTMLCanvasElement[] = [];

    const draw = async () => {
      try {
        const pdfjs = await import('pdfjs-dist');

        /*
          The worker is loaded from our own bundle, not from a CDN.

          `import.meta.url` lets the bundler fingerprint and serve it, which
          matters twice: a CDN worker is a third party able to read every
          policy document the company has, and it is one more thing to be
          offline when somebody needs to sign a policy.
        */
        pdfjs.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/build/pdf.worker.min.mjs',
          import.meta.url,
        ).toString();

        const data = await file.arrayBuffer();
        if (cancelled) return;

        const document_ = await pdfjs.getDocument({ data }).promise;
        if (cancelled) return;
        setPageCount(document_.numPages);

        for (let number = 1; number <= document_.numPages; number += 1) {
          if (cancelled) return;

          const page = await document_.getPage(number);
          /*
            Rendered at the device's own pixel density, then scaled back down
            in CSS. Drawn at CSS pixels a policy is legible on a laptop and
            soft on a phone, which is the device this had to be fixed for.
          */
          const scale = Math.min(globalThis.devicePixelRatio || 1, 2);
          const width = container.current?.clientWidth ?? 800;
          const unscaled = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: (width / unscaled.width) * scale });

          const canvas = window.document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = '100%';
          canvas.style.height = 'auto';
          canvas.style.display = 'block';
          canvas.style.marginBottom = `${space[3]}px`;
          canvas.setAttribute('role', 'img');
          canvas.setAttribute('aria-label', `${title}, page ${number}`);

          const context = canvas.getContext('2d');
          if (!context) throw new Error('this browser cannot draw the document');

          container.current?.append(canvas);
          canvases.push(canvas);

          await page.render({ canvas, canvasContext: context, viewport }).promise;
          if (cancelled) return;
          setPagesDrawn(number);
        }
      } catch (error) {
        if (!cancelled) {
          onFailure(error instanceof Error ? error.message : 'The document could not be drawn.');
        }
      }
    };

    void draw();

    return () => {
      cancelled = true;
      // Canvases hold a bitmap per page, which for a long policy is a lot of
      // memory to leave behind a window somebody has closed.
      for (const canvas of canvases) canvas.remove();
    };
  }, [file, title, onFailure]);

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: space[3] }}>
      <div ref={container} />

      {pagesDrawn === 0 ? (
        <div
          style={{
            height: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin tip="Opening the document" size="large">
            <span />
          </Spin>
        </div>
      ) : pagesDrawn < pageCount ? (
        /* Said rather than spun: the page they are reading is already there,
           and a spinner over it would suggest it is not. */
        <Text type="secondary" style={{ display: 'block', textAlign: 'center' }}>
          Loading page {pagesDrawn + 1} of {pageCount}…
        </Text>
      ) : null}
    </div>
  );
}
